/**
 * Live traffic flow, incident pins, and NEXRAD radar — the same overlays
 * the COP and Pathfinder navigator toolbars share.
 */
import React, { useEffect, useState } from 'react';
import { Layer, Source } from 'react-map-gl';
import type { TrafficIncident, WeatherAlert } from '../../services/areaIntelService';
import MapTrafficIncidents from './MapTrafficIncidents';

export interface MapCopOverlaysProps {
  traffic: boolean;
  radar: boolean;
  radarOpacity?: number;
  incidents?: TrafficIncident[];
  weatherAlerts?: WeatherAlert[];
}

const MapCopOverlays: React.FC<MapCopOverlaysProps> = ({
  traffic,
  radar,
  radarOpacity = 0.58,
  incidents,
  weatherAlerts,
}) => {
  const [epoch, setEpoch] = useState(() => Math.floor(Date.now() / 300_000));

  useEffect(() => {
    if (!radar) return undefined;
    const tick = () => setEpoch(Math.floor(Date.now() / 300_000));
    tick();
    const timer = window.setInterval(tick, 60_000);
    return () => window.clearInterval(timer);
  }, [radar]);

  return (
    <>
      {traffic && (
        <Source id="mapbox-traffic" type="vector" url="mapbox://mapbox.mapbox-traffic-v1">
          <Layer
            id="traffic-flow"
            type="line"
            source-layer="traffic"
            paint={{
              'line-color': [
                'match',
                ['get', 'congestion'],
                'low', '#4CAF50',
                'moderate', '#FFEB3B',
                'heavy', '#FF9800',
                'severe', '#f44336',
                '#888888',
              ],
              'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1.5, 14, 3, 18, 6],
              'line-opacity': 0.8,
            }}
            layout={{ 'line-cap': 'round', 'line-join': 'round' }}
          />
        </Source>
      )}
      <MapTrafficIncidents
        visible={traffic}
        incidents={incidents}
        weatherAlerts={weatherAlerts}
      />
      {radar && (
        <Source
          key={`nexrad-radar-${epoch}`}
          id="nexrad-radar"
          type="raster"
          tiles={[
            `https://mesonet.agron.iastate.edu/cache/tile.py/1.0.0/nexrad-n0q-900913/{z}/{x}/{y}.png?t=${epoch}`,
          ]}
          tileSize={256}
          maxzoom={10}
          attribution="NEXRAD via Iowa Environmental Mesonet"
        >
          <Layer
            id="nexrad-radar-layer"
            type="raster"
            paint={{ 'raster-opacity': radarOpacity, 'raster-fade-duration': 250 }}
          />
        </Source>
      )}
    </>
  );
};

export default MapCopOverlays;
